import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { KeyRound, MessageSquare, MessageCircle, ShieldCheck, Building2, Briefcase, User, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { isMobile, mobileDigits, MOBILE_LENGTH } from "@/lib/phone";
import { useRevealAboveKeyboard } from "@/lib/useRevealAboveKeyboard";

const CHANNELS = {
  sms: { label: "SMS", icon: MessageSquare },
  whatsapp: { label: "WhatsApp", icon: MessageCircle },
};
const ROLE_ICONS = { super_admin: ShieldCheck, tenant_admin: Building2, seller: Briefcase, customer: User };

// Two looks: the black-and-white admin consoles, and the tenant-branded customer app.
const LOOKS = {
  console: {
    label: "admin-overline",
    input: "w-full border border-neutral-300 bg-white px-3 py-3 text-base focus:outline-none focus:border-black",
    icon: "h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500",
    withIcon: "pl-10",
    prefix: "absolute left-3 top-1/2 -translate-y-1/2 text-base text-neutral-500",
    withPrefix: "pl-12",
    primary: "w-full bg-black text-white py-3.5 text-sm font-semibold uppercase tracking-wide hover:bg-neutral-800 disabled:opacity-50 min-h-[48px]",
    secondary: "flex-1 border border-neutral-300 bg-white py-3 px-3 text-xs font-semibold uppercase tracking-wide hover:bg-neutral-50 disabled:opacity-50 min-h-[44px]",
    muted: "text-neutral-500",
    strong: "text-black",
    choice: "border border-neutral-300 bg-white",
    chosen: "border-black bg-black text-white",
    error: "border border-red-200 bg-red-50 text-red-800",
    note: "border border-neutral-200 bg-neutral-50 text-neutral-700",
    option: "w-full text-left border border-neutral-300 bg-white hover:border-black hover:bg-neutral-50 disabled:opacity-50",
    optionIcon: "bg-neutral-100 text-black",
    heading: "admin-heading text-xl font-bold",
  },
  brand: {
    label: "text-xs uppercase tracking-wider font-semibold brand-text-muted",
    input: "w-full brand-surface brand-text brand-border border rounded-2xl px-4 py-3.5 text-base focus:outline-none",
    icon: "h-4 w-4 absolute left-4 top-1/2 -translate-y-1/2 brand-text-muted",
    withIcon: "pl-11",
    prefix: "absolute left-4 top-1/2 -translate-y-1/2 text-base brand-text-muted",
    withPrefix: "pl-14",
    primary: "w-full brand-primary py-4 rounded-full font-semibold disabled:opacity-50 min-h-[48px]",
    secondary: "flex-1 brand-muted brand-text py-3 px-3 rounded-full text-sm font-semibold disabled:opacity-50 min-h-[44px]",
    muted: "brand-text-muted",
    strong: "brand-text",
    choice: "brand-surface brand-text brand-border border rounded-2xl",
    chosen: "brand-primary border-transparent rounded-2xl",
    error: "rounded-2xl bg-red-50 text-red-800 border border-red-200",
    note: "rounded-2xl brand-muted brand-text",
    option: "w-full text-left brand-surface brand-text brand-border border rounded-2xl disabled:opacity-50",
    optionIcon: "brand-muted brand-primary-text rounded-xl",
    heading: "brand-heading text-xl font-bold",
  },
};

const digits = (value) => value.replace(/\D/g, "");

// The server's own words when it gave some; a plain statement when it could not be reached.
const explain = (err, fallback) => {
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  return err?.response ? fallback : "The server could not be reached. Check your connection and try again.";
};

/**
 * Sign-in with a mobile number and a one-time code, used by every sign-in screen.
 *
 *   Enter mobile number → SMS (default) or WhatsApp → Send OTP → enter OTP → Verify
 *   → the server works out what this number may open → open it, or choose
 *
 * The screen never says which role is signing in. After the code is verified the
 * server answers with the accounts that number really has ("destinations"). With
 * one, the person is signed in to it at once. With several, they choose, and the
 * choice goes back to the server to be checked before a session is opened.
 *
 * Props:
 *   slug         the tenant whose site or app this sign-in is on; none on the platform's own sign-in
 *   prefer       on a screen made for one role: "tenant_admin" | "seller" | "super_admin".
 *                Only picks among what the number has; it grants nothing.
 *   look         "console" | "brand"
 *   testid       prefix for data-testid values
 *   withName     also ask for a name (used when a new customer account is created)
 *   onSignedIn   called with the session the server opened: { token, user, destination }
 *   testNumbers  [{ label, phone, code }]: development only, shown when the server is in test mode
 *
 * The mobile number is ten digits, typed without the country code: the field
 * takes digits only and no more than ten.
 */
export default function OtpLogin({ slug, prefer, look = "console", testid, withName = false, onSignedIn, testNumbers }) {
  const ui = LOOKS[look];
  const [config, setConfig] = useState(null);
  const [step, setStep] = useState("phone"); // "phone" | "code" | "choose"
  const [phone, setPhone] = useState("");
  const [name, setName] = useState("");
  const [channel, setChannel] = useState("sms");
  const [sentBy, setSentBy] = useState(null);
  const [code, setCode] = useState("");
  const [wait, setWait] = useState(0);
  const [busy, setBusy] = useState(null); // "send" | "verify" | "select"
  const [error, setError] = useState("");
  const [offer, setOffer] = useState(null); // { ticket, destinations } when the number has more than one destination
  const codeRef = useRef(null);
  const blockRef = useRef(null);
  const submitted = useRef("");
  useRevealAboveKeyboard(blockRef);

  useEffect(() => {
    let current = true;
    api.get("/auth/otp/config").then((r) => {
      if (!current) return;
      setConfig(r.data);
      setChannel(r.data.default_channel || "sms");
    }).catch(() => { if (current) setConfig({ channels: [], unreachable: true }); });
    return () => { current = false; };
  }, []);

  // Resend countdown
  useEffect(() => {
    if (wait <= 0) return undefined;
    const timer = setTimeout(() => setWait((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [wait]);

  useEffect(() => { if (step === "code") codeRef.current?.focus(); }, [step]);

  const channels = config?.channels || [];
  const length = config?.code_length || 6;
  // The number, and where the sign-in is happening. Never a role.
  const account = useMemo(() => ({ phone, ...(slug ? { tenant_slug: slug } : {}) }), [phone, slug]);
  const givenName = withName && name.trim() ? { name: name.trim() } : {};

  const send = useCallback(async (by) => {
    if (!isMobile(phone)) { setError(`Enter your ${MOBILE_LENGTH}-digit mobile number.`); return; }
    setBusy("send"); setError("");
    try {
      const r = await api.post("/auth/otp/send", { ...account, channel: by });
      setSentBy(r.data.channel); setChannel(r.data.channel);
      setWait(r.data.resend_in || 30);
      setCode(""); submitted.current = "";
      setStep("code");
    } catch (err) {
      const retry = err?.response?.data?.retry_after;
      if (retry) setWait(Math.min(retry, 3600));
      setError(explain(err, "The code could not be sent. Please try again."));
    } finally { setBusy(null); }
  }, [phone, account]);

  const finish = async (session) => {
    try { await onSignedIn(session); } catch (err) { setError(err?.message || "Sign-in failed."); setBusy(null); }
  };

  const verify = async (value) => {
    if (value.length !== length) { setError(`Enter the ${length}-digit code.`); return; }
    setBusy("verify"); setError("");
    try {
      const r = await api.post("/auth/otp/verify", { ...account, code: value, ...givenName, ...(prefer ? { prefer } : {}) });
      if (r.data.session) { await finish(r.data.session); return; }
      setOffer({ ticket: r.data.ticket, destinations: r.data.destinations });
      setStep("choose"); setBusy(null);
    } catch (err) {
      setError(explain(err, "That code could not be checked. Please try again."));
      setBusy(null);
    }
  };

  const choose = async (destination) => {
    setBusy("select"); setError("");
    try {
      const r = await api.post("/auth/select", { ticket: offer.ticket, role: destination.role, tenant_slug: destination.tenant_slug, ...givenName });
      await finish(r.data);
    } catch (err) {
      // The ticket lasts a few minutes. After that the number has to be verified again.
      setError(err?.response?.status === 401 ? "That took too long. Please sign in again." : explain(err, "That could not be opened. Please try again."));
      setBusy(null);
    }
  };

  const onCode = (value) => {
    const next = digits(value).slice(0, length);
    setCode(next);
    // A complete code is checked straight away; the same code is not sent twice by itself.
    if (next.length === length && next !== submitted.current && !busy) { submitted.current = next; verify(next); }
  };

  const startAgain = () => { setStep("phone"); setCode(""); setError(""); setSentBy(null); setOffer(null); };
  const other = channels.find((c) => c !== channel);
  const useOther = () => { setError(""); if (wait > 0) setChannel(other); else send(other); };

  const showTest = testNumbers && config?.test_mode;
  const testMatch = showTest ? testNumbers.find((t) => t.phone === phone) : null;
  const countryCode = config?.country_code || "+91";

  if (step === "choose") {
    const none = offer.destinations.length === 1 && offer.destinations[0].is_new; // a role-specific screen, and the number has no account
    return (
      <div ref={blockRef} data-testid={`${testid}-form`}>
        <div className="space-y-4" data-testid={`${testid}-choose`}>
          <div>
            <div className={ui.heading}>{none ? "This number has no account here" : "Where would you like to continue?"}</div>
            <p className={`text-sm mt-1 ${ui.muted}`}>
              {none
                ? <>No admin or seller access is registered for <span className="font-semibold break-anywhere">{countryCode} {phone}</span>. You can continue as a customer, or use another number.</>
                : <>Number verified: <span className="font-semibold break-anywhere">{countryCode} {phone}</span>. It can open:</>}
            </p>
          </div>
          <div className="space-y-2">
            {offer.destinations.map((d) => {
              const Icon = ROLE_ICONS[d.role];
              return (
                <button key={`${d.role}:${d.tenant_slug || ""}`} type="button" disabled={busy === "select"} onClick={() => choose(d)}
                  className={`${ui.option} p-3 flex items-center gap-3 min-h-[64px]`} data-testid={`${testid}-destination-${d.role}${d.tenant_slug ? `-${d.tenant_slug}` : ""}`}>
                  <span className={`h-10 w-10 flex items-center justify-center flex-shrink-0 ${ui.optionIcon}`}><Icon className="h-5 w-5" aria-hidden="true" /></span>
                  <span className="flex-1 min-w-0">
                    <span className="block font-semibold text-sm">{d.title}{d.is_new ? " (new account)" : ""}</span>
                    <span className={`block text-xs break-words ${ui.muted}`}>{d.description}</span>
                  </span>
                  <ChevronRight className={`h-4 w-4 flex-shrink-0 ${ui.muted}`} aria-hidden="true" />
                </button>
              );
            })}
          </div>
          {error && <div className={`${ui.error} px-4 py-3 text-sm break-words`} role="alert" data-testid={`${testid}-error`}>{error}</div>}
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={startAgain} className={ui.secondary} data-testid={`${testid}-change-number`}>Use another number</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div ref={blockRef} data-testid={`${testid}-form`}>
      {step === "phone" ? (
        <form onSubmit={(e) => { e.preventDefault(); send(channel); }} className="space-y-4" noValidate>
          <div>
            <label className={ui.label} htmlFor={`${testid}-phone-input`}>Mobile number</label>
            <div className="mt-1 relative">
              <span className={ui.prefix} aria-hidden="true" data-testid={`${testid}-country-code`}>{countryCode}</span>
              <input id={`${testid}-phone-input`} type="tel" inputMode="numeric" autoComplete="tel-national" value={phone}
                onChange={(e) => { setPhone(mobileDigits(e.target.value)); setError(""); }}
                aria-describedby={`${testid}-phone-help`}
                data-testid={`${testid}-phone`} className={`${ui.input} ${ui.withPrefix}`} placeholder="98XXXXXXXX" />
            </div>
            <div id={`${testid}-phone-help`} className={`text-xs mt-1 ${ui.muted}`}>{MOBILE_LENGTH} digits, without {countryCode}.</div>
          </div>

          {withName && (
            <div>
              <label className={ui.label} htmlFor={`${testid}-name-input`}>Your name</label>
              <input id={`${testid}-name-input`} autoComplete="name" value={name} onChange={(e) => setName(e.target.value.slice(0, 80))}
                data-testid={`${testid}-name`} className={`${ui.input} mt-1`} placeholder="Optional" />
            </div>
          )}

          {channels.length > 1 && (
            <fieldset>
              <legend className={ui.label}>Send OTP via</legend>
              <div className="mt-1 grid grid-cols-2 gap-2" role="radiogroup">
                {channels.map((c) => {
                  const Icon = CHANNELS[c].icon;
                  return (
                    <label key={c} data-testid={`${testid}-channel-${c}`}
                      className={`flex items-center justify-center gap-2 px-3 min-h-[48px] text-sm font-semibold cursor-pointer focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-neutral-500 ${channel === c ? ui.chosen : ui.choice}`}>
                      <input type="radio" name={`${testid}-channel`} value={c} checked={channel === c} onChange={() => setChannel(c)} className="sr-only" />
                      <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" /> {CHANNELS[c].label}
                    </label>
                  );
                })}
              </div>
            </fieldset>
          )}

          {config && channels.length === 0 && (
            <div className={`${ui.error} px-4 py-3 text-sm`} role="alert" data-testid={`${testid}-unavailable`}>
              {config.unreachable ? "The server could not be reached. Check your connection and reload the page." : "Sign-in codes cannot be sent at the moment. Please try again later."}
            </div>
          )}
          {error && <div className={`${ui.error} px-4 py-3 text-sm break-words`} role="alert" data-testid={`${testid}-error`}>{error}</div>}

          <button type="submit" disabled={busy === "send" || !config || channels.length === 0 || wait > 0} className={ui.primary} data-testid={`${testid}-send-otp`}>
            {busy === "send" ? "Sending..." : wait > 0 ? `Send OTP in ${wait}s` : "Send OTP"}
          </button>

          {showTest && (
            <div className={`${ui.note} px-4 py-3 text-xs break-words`} data-testid={`${testid}-test-hint`}>
              <div className="font-semibold">Development test numbers (no message is sent)</div>
              <div className="mt-1 space-y-1">
                {testNumbers.map((t) => (
                  <button key={t.phone} type="button" onClick={() => { setPhone(t.phone); setError(""); }} className="block text-left underline min-h-[28px]" data-testid={`${testid}-use-test-${t.phone}`}>
                    {t.label}: <span className="font-mono">{t.phone}</span>, OTP <span className="font-mono">{t.code}</span>
                  </button>
                ))}
              </div>
              <div className="mt-1">Any of them works on any sign-in screen: the number decides where you go.</div>
            </div>
          )}
        </form>
      ) : (
        <form onSubmit={(e) => { e.preventDefault(); submitted.current = code; verify(code); }} className="space-y-4" noValidate>
          <div className={`text-sm ${ui.strong}`} data-testid={`${testid}-sent-note`}>
            OTP sent via <span className="font-semibold">{CHANNELS[sentBy]?.label}</span> to <span className="font-semibold break-anywhere">{countryCode} {phone}</span>
          </div>
          <div>
            <label className={ui.label} htmlFor={`${testid}-otp-input`}>OTP</label>
            <div className="mt-1 relative">
              <KeyRound className={ui.icon} aria-hidden="true" />
              <input id={`${testid}-otp-input`} ref={codeRef} type="text" inputMode="numeric" pattern="[0-9]*" autoComplete="one-time-code"
                value={code} onChange={(e) => onCode(e.target.value)} maxLength={length}
                data-testid={`${testid}-otp`} className={`${ui.input} ${ui.withIcon} text-lg font-mono tracking-[0.3em]`} placeholder={"•".repeat(length)} />
            </div>
          </div>

          {error && <div className={`${ui.error} px-4 py-3 text-sm break-words`} role="alert" data-testid={`${testid}-error`}>{error}</div>}

          <button type="submit" disabled={busy === "verify"} className={ui.primary} data-testid={`${testid}-verify-otp`}>
            {busy === "verify" ? "Verifying..." : "Verify & Login"}
          </button>

          <div className={`text-sm ${ui.muted}`}>
            Didn't receive it?{" "}
            {wait > 0 ? (
              <span data-testid={`${testid}-countdown`}>Resend OTP{channel !== sentBy ? ` by ${CHANNELS[channel].label}` : ""} in {wait} second{wait === 1 ? "" : "s"}</span>
            ) : (
              <button type="button" onClick={() => send(channel)} disabled={busy === "send"} className={`underline font-semibold ${ui.strong} min-h-[32px]`} data-testid={`${testid}-resend`}>
                {busy === "send" ? "Sending..." : `Resend OTP by ${CHANNELS[channel].label}`}
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={startAgain} className={ui.secondary} data-testid={`${testid}-change-number`}>Change number</button>
            {other && (
              <button type="button" onClick={useOther} disabled={busy === "send"} className={ui.secondary} data-testid={`${testid}-switch-channel`}>
                Use {CHANNELS[other].label} instead
              </button>
            )}
          </div>
          {testMatch && <div className={`text-xs ${ui.muted}`} data-testid={`${testid}-test-code`}>Development test number ({testMatch.label}): OTP <span className="font-mono">{testMatch.code}</span></div>}
        </form>
      )}
    </div>
  );
}
